/* eslint-disable max-len */
import React, { forwardRef, memo, useState, useMemo, useCallback, useEffect, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import Promise from 'bluebird';
import _ from 'underscore';
import classnames from 'classnames';
import $ from 'jquery';

import { getAdCreatorInstrumentation } from '@bingads-webui-campaign/ad-creator-tools-instrumentation';
import { TooltipHost, ActionButton } from '@bingads-webui-react/fabric-control';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  attachAIGenAssetContextualAttribute,
} from '@bingads-webui-campaign/responsive-search-ad-strength';
import { isPlayPausePreviewImageEnabled } from '@bingads-webui-campaign-react/play-pause-buttons';

import {
  useImageExperienceAdPreviewModel,
  AdPreviewCarouselLayoutEx,
  AdPreviewSinglePlacementLayout,
} from '@bingads-webui-campaign-react/unified-ad-preview-library-cmui';

import getCroppedImage from '../util/get-cropped-image';
import getGenericImageObjectAsync from '../util/get-generic-image-object';
import C from '../constants';
import { ImageExperienceImage } from '../model/image-experience-image';
import { StyledAdPlacementsGridPreviewPanel } from './styled-ad-placements-grid-preview-panel';
import { getPreviewCarouselStyle } from '../../style/js/preview-carousel-style';

import {
  getPlacementTitle as getPlacementTitleImpl,
  getPlacementTitleInViewAll,
} from '../ad-preview-library-wrapper/util';
import { getOnRenderPlacement } from './unified-placement-render';

export const isInvalidImageSize = (image, minSize, isAdCreatorEnabled) => {
  if (!image) {
    return false;
  }

  let widthToCheck = image.Width;
  let heightToCheck = image.Height;

  if (isAdCreatorEnabled
    && image.BaseImage
    && image.BaseImage.CropSettings
    && image.BaseImage.CropSettings.SourceWidth > 0
    && image.BaseImage.CropSettings.SourceHeight > 0) {
    widthToCheck = image.BaseImage.CropSettings.SourceWidth;
    heightToCheck = image.BaseImage.CropSettings.SourceHeight;
  }

  return image && minSize && (widthToCheck < minSize[0] || heightToCheck < minSize[1]);
};

export const UnStyledPreviewCarousel = forwardRef(({
  adPreviewConfig,

  imageList,
  genericImage: genericImageProp,
  placeHolderImages: placeHolderImagesProp,
  totalImageListCount,
  previewTexts,

  enableAutoRotate,
  disablePlacementActions,
  isAdCreatorEnabled,
  isMultimediaAd,
  isImageAdExtensionMode,
  isImageExtensionsEnhancementsEnabled,
  isImageFittingEnabled,
  // eligibleLayout,

  onAddAspectRatio: onAddAspectRatioProp,
  onCropImage: onCropImageProp,
  onReplaceImage: onReplaceImageProp,
  onRemoveAspectRatio: onRemoveAspectRatioProp,
  onUsedViewAllPlacements,

  customClasses,
  permissions,
  instrumentation,
  adCreatorScenario,
  i18n,
  newI18n,
  classes,
}, ref) => {
  // states
  const [activeIndex, setActiveIndex] = useState(0);
  const [defaultImages, setDefaultImages] = useState([]);
  const [placeHolderImages, setPlaceHolderImages] = useState([]);
  const [isViewAllPanelOpen, setIsViewAllPanelOpen] = useState(false);

  const isMultiMediaAdsPhase2Enabled = permissions && permissions.IsMultiMediaAdsPhase2Enabled;
  const isRecommendationTypeImproveMultiMediaAdsEnabled = true;

  // image extension features are being released after AdCreatorV2 has been GAed
  const enableCreatorFeatures =
    (isAdCreatorEnabled && !isImageAdExtensionMode) || (isImageAdExtensionMode && isImageExtensionsEnhancementsEnabled);

  const isSelectedImageLogo = useMemo(() => {
    const { isLogo: isDefaultImageLogo, Type } = (defaultImages && defaultImages[activeIndex]) || {};
    const image = _.find(imageList, imageObj => imageObj.Type === Type);
    if (image) {
      return image.isLogo;
    }
    return isDefaultImageLogo;
  }, [defaultImages, activeIndex, imageList]);

  const imageListForDisplay = useMemo(() => {
    // if defaultImages is not empty, sort image list by defaultImages order
    const theImageListForDisplay = !_.isEmpty(defaultImages) ? _.map(defaultImages, (defaultImage) => {
      const imgSet = _.find(imageList, imageObj => imageObj.Type === defaultImage.Type);

      if (imgSet) {
        return imgSet;
      }

      return defaultImage;
    }) : imageList;

    if (isSelectedImageLogo) {
      _.each(theImageListForDisplay, (imageObj) => {
        imageObj.isLogo = true; // eslint-disable-line no-param-reassign
      });
    }

    return theImageListForDisplay;
  }, [defaultImages, isSelectedImageLogo, imageList]);

  const aspectRatios = useMemo(() => {
    const curAspectRatios = isMultimediaAd ?
      C.getMmaAspectRatios(isSelectedImageLogo ?
        C.imageOptionKeys.Logo : C.imageOptionKeys.Image) : C.aspectRatios;
    return curAspectRatios;
  }, [isMultimediaAd, isSelectedImageLogo]);

  useEffect(() => {
    if (_.result(_.first(imageListForDisplay), 'isRecommendedImage')) {
      // Registers AI generated asset contextual attribute popup to listen for key/mouse events
      const element = $('.iconba.iconba-Lightbulb.new-preview-carousel');
      attachAIGenAssetContextualAttribute(_.extend({
        element,
        i18n,
        improveMMAOption: { type: 'Image' },
        newI18n,
      }));
    }
  }, [i18n, imageListForDisplay, newI18n]);

  const setupImagesAsync = useCallback((aspectRatiosOrder) => {
    const imagesToSetup = [
      genericImageProp,
      ...placeHolderImagesProp,
    ];

    const setupPromises = _.map(imagesToSetup, imageToSetup => getGenericImageObjectAsync(imageToSetup).then((imageObj) => {
      let croppedImages = getCroppedImage({
        originalImageToCrop: imageObj,
        isMultimediaAd,
      });
      croppedImages = _.sortBy(croppedImages, image => aspectRatiosOrder.indexOf(image.type));
      _.each(croppedImages, (image) => {
        image.isIncluded = false; // eslint-disable-line no-param-reassign
      });
      return croppedImages;
    }));

    Promise.all(setupPromises).then(([defaultImages1, ...placeHolderImagesList]) => {
      setDefaultImages(defaultImages1);
      setPlaceHolderImages(_.isEmpty(placeHolderImagesList) ? [defaultImages1] : placeHolderImagesList);
    });
  }, [genericImageProp, isMultimediaAd, placeHolderImagesProp]);

  useEffect(() => {
    const aspectRatiosOrder = C.getImageTypesWithOrder(isMultimediaAd);
    setupImagesAsync(aspectRatiosOrder);
  }, [isMultimediaAd, setupImagesAsync]);

  const {
    imageExperiencePlacementSets: placementSets,
    onUpdateImageExperienceImageList,
  } = useImageExperienceAdPreviewModel(adPreviewConfig.placementConfig, null, isImageFittingEnabled);

  const placementList = useMemo(() => {
    // should only have one placement set
    const list = _.chain(placementSets).map(placementSet => placementSet.placementList).flatten().value();
    return list || [];
  }, [placementSets]);

  useEffect(() => {
    // placeholder images are used in multi-images placements
    const newImageList = [imageListForDisplay, ...placeHolderImages];
    onUpdateImageExperienceImageList(newImageList, null);
  }, [imageListForDisplay, onUpdateImageExperienceImageList, placeHolderImages]);

  const [onRenderPlacement, onRenderPlacementGridView] = useMemo(() => {
    const params = {
      isMultimediaAd,
      isMultiMediaAdsPhase2Enabled,
      opacityChangeEnabled: isMultimediaAd,
      previewTexts,
      i18n,
      permissions,
    };
    return [
      getOnRenderPlacement(params, classes.placementContainerRoot),
      getOnRenderPlacement(params, classes.placementContainerRootGridView),
    ];
  }, [classes, i18n, isMultiMediaAdsPhase2Enabled, isMultimediaAd, previewTexts, permissions]);

  const getPlacementTitle = useCallback((placementIndex, isInViewAllPanel = false) => {
    if (isInViewAllPanel) {
      return getPlacementTitleInViewAll(adPreviewConfig, placementIndex, i18n);
    }
    return getPlacementTitleImpl(adPreviewConfig, placementIndex, i18n);
  }, [adPreviewConfig, i18n]);

  const setPreviewIndexByType = useCallback((type) => {
    const currentIndex = _.findIndex(placementList, ({ placementMeta }) =>
      type && placementMeta && placementMeta.imageType && placementMeta.imageType.toLowerCase()=== type.toLowerCase());
    if (currentIndex >= 0) {
      setActiveIndex(currentIndex);
    }
  }, [placementList]);

  useImperativeHandle(ref, () => ({
    setPreviewIndexByType,
  }));

  const getEditingImageIndex = useCallback(
    e => (_.isUndefined(e.target.dataset.index) ? activeIndex : parseInt(e.target.dataset.index, 10)),
    [activeIndex]
  );

  const onAddAspectRatio = useCallback((e) => {
    const idx = getEditingImageIndex(e);
    onAddAspectRatioProp(imageListForDisplay[idx].type);
  }, [getEditingImageIndex, imageListForDisplay, onAddAspectRatioProp]);

  const onCrop = useCallback((e) => {
    const index = getEditingImageIndex(e);
    const { placementMeta } = placementList[index] || {};
    const { placementType } = placementMeta || {};

    if (enableCreatorFeatures) {
      setIsViewAllPanelOpen(false);
      onCropImageProp(imageListForDisplay[index], placementType);
    } else {
      onCropImageProp(imageListForDisplay[index], placementType);
    }

    if (isAdCreatorEnabled) {
      const { editPlacementActivity } =
        getAdCreatorInstrumentation(instrumentation, adCreatorScenario);

      editPlacementActivity.trace('User opened edit placement panel');
    }
  }, [adCreatorScenario, enableCreatorFeatures, getEditingImageIndex, imageListForDisplay, instrumentation, isAdCreatorEnabled, onCropImageProp, placementList]);

  const onReplace = useCallback((e) => {
    const index = getEditingImageIndex(e);
    setIsViewAllPanelOpen(false);
    onReplaceImageProp(imageListForDisplay[index]);
  }, [getEditingImageIndex, imageListForDisplay, onReplaceImageProp]);

  const onRemoveAspectRatio = useCallback((e) => {
    const index = getEditingImageIndex(e);
    onRemoveAspectRatioProp(imageListForDisplay[index].type);
  }, [getEditingImageIndex, imageListForDisplay, onRemoveAspectRatioProp]);

  const getAdPreviewTitle = useCallback(() => getPlacementTitle(activeIndex), [activeIndex, getPlacementTitle]);

  const isPlacementActionsEnabled = useMemo(
    () => totalImageListCount !== 0 && !disablePlacementActions,
    [disablePlacementActions, totalImageListCount]
  );

  const getValidationMessage = useCallback((image) => {
    if (_.result(image, 'isIncluded') !== true) {
      const isSupported = image && aspectRatios[image.type];
      const minSize = image && _.result(aspectRatios[image.type], 'minSize');

      if (!isSupported) {
        return (
          <span className="action-label">
            { i18n.getString('ImageEditor_Not_Supported') }
          </span>
        );
      }

      return isInvalidImageSize(image, minSize, isAdCreatorEnabled) &&
        <span className="action-label">
          {
            i18n.getString('ImageEditor_SizeValidationFailTemplate', {
              width: minSize[0],
              height: minSize[1],
            })
          }
        </span>;
    }

    return null;
  }, [aspectRatios, i18n, isAdCreatorEnabled]);

  const getAdPreviewButtonsForAdCreator = useCallback((cellIndex = -1) => {
    if (isMultimediaAd && !_.isEmpty(imageList)) {
      const selectedIndex = cellIndex > -1 ? cellIndex : activeIndex;
      const selectedImage = imageListForDisplay[selectedIndex];
      const isRequired = selectedImage && _.result(aspectRatios[selectedImage.type], 'minimumRequirements') > 0 && totalImageListCount === 1;
      const minSize = selectedImage && _.result(aspectRatios[selectedImage.type], 'minSize');
      const isSupported = selectedImage && aspectRatios[selectedImage.type];

      if (!isSupported) {
        return null;
      }

      if (isInvalidImageSize(selectedImage, minSize, isAdCreatorEnabled)) {
        return null;
      }

      return (
        <div className={classnames('ad-preview-actions', 'inline-title')}>
          {_.result(selectedImage, 'isIncluded') !== true ?
            (
              <button
                className="include-aspect-ratio-button btn btn-default"
                onClick={onAddAspectRatio}
                data-index={selectedIndex}
              >
                { i18n.getString('ImageEditor_Include_AspectRatio') }
              </button>
            ) :
            (
              <React.Fragment>
                {!_.result(selectedImage, 'disableEdit') &&
                  <button
                    className="crop-image-button btn btn-default"
                    onClick={onCrop}
                    data-index={selectedIndex}
                  >
                    {i18n.getString('ImageExperience_Edit_Aspect_Ratio')}
                  </button>
                }
                {isRequired &&
                  <TooltipHost
                    content={i18n.getString('ImageExperience_Image_Placement_Required_Message')}
                    calloutProps={{ gapSpace: 8 }}
                  >
                    <button
                      className="exclude-aspect-ratio-button btn btn-default"
                      onClick={onRemoveAspectRatio}
                      disabled
                      data-index={selectedIndex}
                    >
                      { i18n.getString('ImageEditor_Exclude_AspectRatio') }
                    </button>
                  </TooltipHost>
                }
                {!isRequired &&
                  <button
                    className="exclude-aspect-ratio-button btn btn-default"
                    onClick={onRemoveAspectRatio}
                    data-index={selectedIndex}
                  >
                    { i18n.getString('ImageEditor_Exclude_AspectRatio') }
                  </button>
                }
              </React.Fragment>
            )
          }
        </div>
      );
    } else if (isImageExtensionsEnhancementsEnabled && !_.isEmpty(imageList)) {
      return (
        <div>
          <button
            className={classnames('crop-image-button btn btn-default', customClasses.reCropButton)}
            onClick={onCrop}
            data-index={cellIndex}
          >
            { i18n.getString('ImageEditor_CropImage') }
          </button>
          <button
            className="replace-image-button btn btn-default"
            onClick={onReplace}
            data-index={cellIndex}
          >
            { i18n.getString('ImageExperience_Replace')}
          </button>
        </div>
      );
    }
    return null;
  }, [activeIndex, aspectRatios, customClasses.reCropButton, i18n, imageList, imageListForDisplay, isAdCreatorEnabled, isImageExtensionsEnhancementsEnabled, isMultimediaAd, onAddAspectRatio, onCrop, onRemoveAspectRatio, onReplace, totalImageListCount]);

  const getAdPreviewActions = useCallback(() => {
    if (isMultimediaAd && !_.isEmpty(imageList)) {
      const selectedImage = imageListForDisplay[activeIndex];
      const validationMsgElement = getValidationMessage(selectedImage);

      return (
        <div className="ad-preview-actions">
          {_.result(selectedImage, 'isIncluded') !== true ?
            (
              <React.Fragment>
                { validationMsgElement }
                { !validationMsgElement && !isAdCreatorEnabled &&
                  <React.Fragment>
                    <span className="action-label">
                      { i18n.getString('ImageEditor_Include_AspectRatio_Label') }
                    </span>
                    <button
                      className="include-aspect-ratio-button btn btn-default"
                      onClick={onAddAspectRatio}
                    >
                      { i18n.getString('ImageEditor_Include_AspectRatio') }
                    </button>
                  </React.Fragment>
                }
              </React.Fragment>
            ) :
            (
              !isAdCreatorEnabled &&
              <React.Fragment>
                <span className="action-label">
                  { _.result(selectedImage, 'isLogo') ?
                    i18n.getString('ImageEditor_CropLogo_Label') :
                    i18n.getString('ImageEditor_CropImage_Label') }
                </span>
                {!_.result(selectedImage, 'disableEdit') &&
                  <button
                    className="crop-image-button btn btn-default"
                    onClick={onCrop}
                  >
                    { i18n.getString('ImageEditor_CropImage') }
                  </button>
                }
                <button
                  className="exclude-aspect-ratio-button btn btn-default"
                  onClick={onRemoveAspectRatio}
                >
                  { i18n.getString('ImageEditor_Exclude_AspectRatio') }
                </button>
              </React.Fragment>
            )
          }
        </div>
      );
    }
    return null;
  }, [activeIndex, getValidationMessage, i18n, imageList, imageListForDisplay, isAdCreatorEnabled, isMultimediaAd, onAddAspectRatio, onCrop, onRemoveAspectRatio]);

  const getAdPreviewHeader = useCallback(() => {
    const enableActionBtn = isPlacementActionsEnabled;

    return isAdCreatorEnabled && isMultimediaAd ?
      (
        <div className="preview-header">
          <div className={classnames('ad-preview-title', 'with-additional-tag')}>
            {getAdPreviewTitle()}
          </div>
          <div className="flex-grow" />
          {
            enableActionBtn && getAdPreviewButtonsForAdCreator()
          }
        </div>
      ) : (
        <div className="ad-preview-title">
          {getAdPreviewTitle()}
        </div>
      );
  }, [getAdPreviewButtonsForAdCreator, getAdPreviewTitle, isAdCreatorEnabled, isMultimediaAd, isPlacementActionsEnabled]);

  const getAdPreviewHeaderInternal = useCallback(({ title, index, enableActionBtn }) => {
    const selectedImage = imageListForDisplay[index];
    const isSelectedImageIncluded = _.result(selectedImage, 'isIncluded');
    const minSize = selectedImage && aspectRatios[selectedImage.type].minSize;
    const hideActionBtn = !enableActionBtn
      || (!isSelectedImageIncluded && isInvalidImageSize(selectedImage, minSize, isAdCreatorEnabled));

    return (
      <div className="preview-header">
        <div className={classnames('ad-preview-title', 'with-additional-tag')}>
          {title}
        </div>
        <div className="flex-grow" />
        {!hideActionBtn &&
          <div className={classnames('ad-preview-actions', 'inline-title')}>
            {getAdPreviewButtonsForAdCreator(index)}
          </div>
        }
      </div>
    );
  }, [aspectRatios, getAdPreviewButtonsForAdCreator, imageListForDisplay, isAdCreatorEnabled]);

  const enableViewAllBtn = isPlacementActionsEnabled && enableCreatorFeatures;

  return (
    <div className={classnames('new-preview-carousel-container', isPlayPausePreviewImageEnabled(permissions) ? classes.PlayPauseButtonsContainer : null)}>
      {getAdPreviewHeader()}
      {
        isRecommendationTypeImproveMultiMediaAdsEnabled
        && _.result(_.first(imageListForDisplay), 'isRecommendedImage') &&
        <div className="improve-mma-title-container">
          <span
            aria-label={i18n.getString('Ad_Strength_RSA_AI_Generated_Asset')}
            className="iconba iconba-Lightbulb new-preview-carousel"
          />
          <span>{newI18n.getString(_TL_('Recommended image'))}</span>
        </div>
      }
      {getAdPreviewActions()}
      <AdPreviewCarouselLayoutEx
        activeIndex={activeIndex}
        setActiveIndex={setActiveIndex}
        placementList={placementList}
        onRenderPlacement={onRenderPlacement}
        enableAutoRotate={enableAutoRotate}
        startAutoRotating={!window.e2eTestEnvironment} // disable auto-rotate in test env
        i18n={i18n}
        customCarouselLayoutStyles={enableCreatorFeatures ? 'new-carousel-navigation-mma' : ''}
        customPlayPauseButtonsStyles={classnames(classes.PlayPauseButtons, 'play-pause-mma')}
        permissions={permissions}
        resetOn={placementList.length} // reset the carousel placement count changes for image extension
      />
      <div className="disclaimer-info">
        <div className="disclaimer-info-inline">
          {isMultimediaAd ? i18n.getString('ImagePreview_Disclaimer_Message_MultimediaAd') : i18n.getString('ImagePreview_Disclaimer_Message')}
        </div>
      </div>
      { enableViewAllBtn &&
        <ActionButton
          className={classnames('view-placements', customClasses.viewAllPlacementsButton)}
          onRenderIcon={() => (<span className="iconba iconba-FullView" />)}
          onClick={() => {
            const { viewAllPlacementsActivity } = getAdCreatorInstrumentation(instrumentation, adCreatorScenario); // eslint-disable-line max-len

            viewAllPlacementsActivity.trace('UserOpenedViewAllPlacements');
            onUsedViewAllPlacements();
            setIsViewAllPanelOpen(true);
          }}
        >
            { i18n.getString('ImageExperience_View_Placements_Example') }
        </ActionButton>
      }
      {/* This grid preview is another layout of carousel preview */}
      { enableViewAllBtn &&
        <StyledAdPlacementsGridPreviewPanel
          isOpen={isViewAllPanelOpen}
          onDismiss={() => {
            setIsViewAllPanelOpen(false);
          }}
          className={classes.gridPreviewContainer}
          cellsNumberPerRow={2}
          totalCount={_.size(imageListForDisplay)}
          cellHeight={510}
          onRenderCell={(cellIndex) => {
            const imageAsset = imageListForDisplay[cellIndex];

            return (
              <div className="placements-grid-preview-cell">
                {getAdPreviewHeaderInternal({
                  title: getPlacementTitle(cellIndex, true),
                  index: cellIndex,
                  enableActionBtn: true,
                })}
                {getValidationMessage(imageAsset)}
                {placementList && placementList[cellIndex] ? (
                  <AdPreviewSinglePlacementLayout
                    placementData={placementList[cellIndex]}
                    onRenderPlacement={onRenderPlacementGridView}
                    hasContainer={false}
                  />
                ) : null}
              </div>
            );
          }}
        />
      }
    </div>
  );
});

UnStyledPreviewCarousel.propTypes = {
  previewTexts: PropTypes.shape({
    longHeadline: PropTypes.string,
    description: PropTypes.string,
    businessName: PropTypes.string,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
    parseDecimal: PropTypes.func.isRequired,
  }).isRequired,
  permissions: PropTypes.shape({
    IsMultiMediaAdsPhase2Enabled: PropTypes.bool,
  }),
  genericImage: PropTypes.string.isRequired,
  imageList: PropTypes.arrayOf(PropTypes.instanceOf(ImageExperienceImage)).isRequired,
  placeHolderImages: PropTypes.arrayOf(PropTypes.string),
  isImageAdExtensionMode: PropTypes.bool,
  // eligibleLayout: PropTypes.string,
  isMultimediaAd: PropTypes.bool,
  onCropImage: PropTypes.func,
  onAddAspectRatio: PropTypes.func,
  onRemoveAspectRatio: PropTypes.func,
  isAdCreatorEnabled: PropTypes.bool,
  enableAutoRotate: PropTypes.bool,
  instrumentation: PropTypes.shape({
    addScenario: PropTypes.func,
  }),
  totalImageListCount: PropTypes.number,
  adCreatorScenario: PropTypes.shape({
    scenarioName: PropTypes.string,
    scenarioValue: PropTypes.string,
  }),
  classes: PropTypes.shape({
    gridPreviewContainer: PropTypes.string,
    PlayPauseButtons: PropTypes.string,
    PlayPauseButtonsContainer: PropTypes.string,
  }),
  disablePlacementActions: PropTypes.bool,
  onUsedViewAllPlacements: PropTypes.func,
  isImageExtensionsEnhancementsEnabled: PropTypes.bool,
  onReplaceImage: PropTypes.func,
  customClasses: PropTypes.shape({
    viewAllPlacementsButton: PropTypes.string,
    reCropButton: PropTypes.string,
  }),
  newI18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }),
  isImageFittingEnabled: PropTypes.bool,
};

UnStyledPreviewCarousel.defaultProps = {
  isImageAdExtensionMode: false,
  isMultimediaAd: false,
  // eligibleLayout: '',
  placeHolderImages: [],
  onCropImage: _.noop,
  onReplaceImage: _.noop,
  onAddAspectRatio: _.noop,
  onRemoveAspectRatio: _.noop,
  permissions: {},
  enableAutoRotate: false,
  isAdCreatorEnabled: false,
  instrumentation: {
    addScenario: _.noop,
  },
  totalImageListCount: 0,
  adCreatorScenario: null,
  classes: {},
  disablePlacementActions: false,
  onUsedViewAllPlacements: _.noop,
  isImageExtensionsEnhancementsEnabled: false,
  customClasses: {},
  newI18n: {
    getString: _.noop,
  },
  isImageFittingEnabled: false,
};

export const UnifiedPreviewCarousel = memo(forwardRef((props, ref) => {
  const classes = useDefaultStyles(getPreviewCarouselStyle);
  return (<UnStyledPreviewCarousel ref={ref} {...props} classes={{ ...classes, ...props.classes }} />);
}));
